import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useClient } from "@/context/ClientContext";
import { usageSummary, getBillingPlans, startCheckout } from "@/lib/api";
import { Lightning, Check, Headset } from "@phosphor-icons/react";
import { toast } from "sonner";

const NEXT_PLAN = { founding_beta: "starter", starter: "growth", growth: "agency" };
const BENEFITS = {
  starter: ["More AI generations every month", "AI Video Studio", "Higher campaign limits", "All AI marketing managers"],
  growth: ["Up to 5x more AI generations", "More AI videos & monthly campaigns", "Advanced automation features", "Priority support"],
  agency: ["Highest AI generation limits", "Unlimited workspaces", "Staff & permissions", "Dedicated success manager"],
};

export function UpgradeDialog() {
  const { activeId } = useClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [rec, setRec] = useState(null);
  const [price, setPrice] = useState(null);
  const [busy, setBusy] = useState(false);
  const isSupport = /temporarily unavailable/i.test(message);

  useEffect(() => {
    const onLimit = async (e) => {
      const msg = e?.detail?.message || "You've reached the AI generation limit for your current plan.";
      setMessage(msg);
      setOpen(true);
      setRec(null);
      setPrice(null);
      try {
        const [sum, plans] = await Promise.all([
          activeId ? usageSummary(activeId) : Promise.resolve(null),
          getBillingPlans(),
        ]);
        const next = NEXT_PLAN[sum?.plan] || null;
        setRec(next);
        if (next) {
          const p = (plans?.plans || []).find((x) => x.id === next || x.plan_id === next);
          setPrice(p?.monthly ?? null);
        }
      } catch { /* dialog still works without plan intel */ }
    };
    window.addEventListener("vozelto:plan-limit", onLimit);
    return () => window.removeEventListener("vozelto:plan-limit", onLimit);
  }, [activeId]);

  const upgradeNow = async () => {
    if (!rec || !activeId) { setOpen(false); navigate("/billing"); return; }
    setBusy(true);
    try {
      const r = await startCheckout({ plan_id: rec, interval: "monthly", workspace_id: activeId, origin_url: window.location.origin });
      if (r.url) { window.location.href = r.url; return; }
      toast.error("Could not start checkout");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      navigate("/billing");
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const recLabel = rec ? rec.charAt(0).toUpperCase() + rec.slice(1) : null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md bg-[#111116] opacity-100 backdrop-blur-xl border-white/10 text-white" data-testid="upgrade-dialog">
        <div className="flex flex-col items-start gap-4">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${isSupport ? "bg-white/10" : "bg-[#FF5E00]/15"}`}>
            {isSupport ? <Headset size={22} className="text-white" /> : <Lightning size={22} weight="fill" className="text-[#FF5E00]" />}
          </div>
          <div>
            <DialogTitle className="font-head text-lg font-semibold text-white" data-testid="upgrade-dialog-title">
              {isSupport ? "AI temporarily unavailable" : "You've reached a plan limit"}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#b8b8b8] mt-1.5" data-testid="upgrade-dialog-message">{message}</DialogDescription>
          </div>

          {!isSupport && (
            <div className="w-full rounded-xl border border-[#FF5E00]/25 bg-[#FF5E00]/5 p-4">
              <div className="text-sm font-semibold mb-2.5">
                {recLabel ? <>Upgrade to <span className="text-[#FF5E00]">{recLabel}</span>{price ? ` — $${price}/mo` : ""} to immediately unlock:</> : "Upgrade your plan to immediately unlock:"}
              </div>
              <ul className="space-y-1.5">
                {(BENEFITS[rec] || BENEFITS.starter).map((b) => (
                  <li key={b} className="flex items-center gap-2 text-sm text-[#d9d9d9]">
                    <Check size={14} weight="bold" className="text-[#FF5E00] shrink-0" /> {b}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex items-center gap-2 w-full pt-1">
            {isSupport ? (
              <Button className="flex-1 bg-white/10 hover:bg-white/15" onClick={() => setOpen(false)} data-testid="upgrade-dialog-close">
                Got it
              </Button>
            ) : (
              <>
                <Button className="flex-1 bg-[#FF5E00] hover:bg-[#ff7a2b] text-black font-semibold" disabled={busy}
                  onClick={upgradeNow} data-testid="upgrade-now-btn">
                  {busy ? "Opening checkout…" : "Upgrade Now"}
                </Button>
                <Button variant="outline" className="border-white/15" data-testid="upgrade-see-plans-btn"
                  onClick={() => { setOpen(false); navigate("/billing"); }}>
                  See all plans
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
